import cn from 'classnames';
import { useCallback, useRef, type ReactNode } from 'react';
import type { Settings } from 'react-slick';
import BaseSlider from 'react-slick';

import type { HeadingLevel } from '@sravni/react-design-system/dist/types/components/Typography/types/Heading.types';
import { Button } from '@sravni/react-design-system/lib/Button';
import { Icon } from '@sravni/react-design-system/lib/Icon';
import { Space } from '@sravni/react-design-system/lib/Space';
import { Heading, Text } from '@sravni/react-design-system/lib/Typography';
import { ArrowLeft, ArrowRight } from '@sravni/react-icons';
import { useIsMobile } from '@sravni/react-utils';

import styles from './medium.module.scss';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const sliderSettings: Settings = {
  dots: false,
  slidesToShow: 1,
  arrows: false,
  infinite: true,
  dotsClass: styles.dots,
  autoplay: false,
  autoplaySpeed: 7000,
};

interface SliderProps extends React.ComponentProps<typeof BaseSlider> {
  className?: string;
  heading: ReactNode;
  headingLevel?: HeadingLevel;
  subtitle?: ReactNode;
  buttonsClassName?: string;
  wrapperClassName?: string;
}

export const SliderMedium = ({
  className,
  heading,
  headingLevel = 2,
  subtitle,
  buttonsClassName,
  wrapperClassName,
  ...props
}: SliderProps) => {
  const isMobile = useIsMobile();
  const sliderRef = useRef<BaseSlider>(null);

  const handleNext = useCallback(() => {
    sliderRef.current?.slickNext();
  }, []);
  const handlePrev = useCallback(() => {
    sliderRef.current?.slickPrev();
  }, []);

  return (
    <Space direction="vertical" size={isMobile ? 16 : 24} className={wrapperClassName}>
      <Space justify="space-between" align="center">
        <Space direction="vertical" size={8}>
          <Heading level={headingLevel}>{heading}</Heading>
          {!!subtitle && <Text size={16}>{subtitle}</Text>}
        </Space>
        <Space size={16} justify="end">
          <Button variant="secondary" onClick={handlePrev} className={cn(styles.button, buttonsClassName)}>
            <Icon icon={<ArrowLeft />} />
          </Button>
          <Button variant="secondary" onClick={handleNext} className={cn(styles.button, buttonsClassName)}>
            <Icon icon={<ArrowRight />} />
          </Button>
        </Space>
      </Space>
      <BaseSlider ref={sliderRef} className={cn(className, styles.slider)} {...sliderSettings} {...props} />
    </Space>
  );
};
